<template>
  <section
    class="member-benefits"
    data-ui="member-benefits"
  >
    <h2
      v-if="heading"
      class="member-benefits__heading"
      v-html="heading"
    />
    <p
      v-if="body"
      class="member-benefits__subheading"
      v-html="body"
    />
    <featured-benefits
      :benefits="featured"
      @click="handleFeaturedBenefitClick"
    />
    <div
      class="member-benefits__wrapper"
      data-analytics-config="exclude-all"
    >
      <template v-if="accordionData && accordionData.length > 0">
        <cdr-accordion-group>
          <cdr-accordion
            v-for="({ links, opened, isNew, badgeText }, index) in accordionData"
            :id="`benefit-${index}`"
            :key="index"
            :opened="opened"
            level="3"
            class="member-benefits__resource-item"
            @accordion-toggle="() => toggleItem(index)"
          >
            <template #label>
              <div>
                {{ accordionData[index].heading }}
                <div
                  v-if="isNew"
                  class="member-benefits__new-benefit"
                >
                  <span>{{ badgeText || 'NEW' }}</span>
                </div>
              </div>
            </template>
            <cdr-text
              class="member-benefits__resource-content"
              tag="p"
              v-html="accordionData[index].body"
            />
            <cdr-link
              v-for="(link, i) in links"
              :key="`link-${i}`"
              :href="link?.target"
              class="member-benefits__resource-link"
              @click="() => handleAccordionLinkClick(index, link?.text)"
            >
              {{ link?.text }}
            </cdr-link>
          </cdr-accordion>
        </cdr-accordion-group>
      </template>
    </div>
  </section>
</template>

<script>
import {
  CdrAccordion, CdrAccordionGroup, CdrLink, CdrText,
} from '@rei/cedar';
import FeaturedBenefits from './FeaturedBenefits.vue';
import { metricsLink } from '../../../utils/analytics';

export default {
  name: 'MemberBenefits',
  components: {
    CdrAccordion,
    CdrText,
    CdrAccordionGroup,
    CdrLink,
    FeaturedBenefits,
  },
  props: {
    image: {
      type: Object,
      required: true,
    },
    body: {
      type: String,
      required: true,
    },
    featured: {
      type: Array,
      required: true,
    },
    heading: {
      type: String,
      required: true,
    },
    standard: {
      type: Array,
      required: true,
    },
  },
  emits: ['modal-open'],
  data() {
    return {
      /* this is required to control accordion state */
      accordionData: this.setupAccordionData(this.standard),
    };
  },
  watch: {
    benefits: {
      handler(newValue) {
        this.accordionData = this.setupAccordionData(newValue);
      },
    },
  },
  methods: {
    /* c8 ignore start */
    handleAccordionLinkClick(index, text) {
      metricsLink(`${this.getLinkName(index)}_${text}`);
    },
    /* c8 ignore end */
    handleFeaturedBenefitClick(benefit) {
      this.$emit('modal-open', benefit);
    },
    toggleItem(index) {
      const nextOpened = !this.accordionData[index].opened;

      this.accordionData[index].opened = nextOpened;
      if (nextOpened) {
        const linkName = this.getLinkName(index);
        /* c8 ignore next */
        metricsLink(linkName);
      }
    },
    getLinkName(index) {
      const label = this.accordionData[index].heading;
      return `member benefits_${index + 1}_${label}`;
    },
    setupAccordionData(benefits) {
      return benefits.map((benefit) => ({
        ...benefit,
        opened: false,
      }));
    },
  },
};
</script>

<style lang="scss">
@import '../../../../style/common/paths.scss';

.member-benefits {
  @include cdr-container;
  position: relative;
  @include cdr-md-mq-up {
    margin-bottom: 8rem;
  }
}
.member-benefits__wrapper {
  @include cdr-container;
  @include cdr-sm-mq-up {
    max-width: 609px;
    padding-left: 0;
    padding-right: 0;
  }
}

.member-benefits__heading {
  position: relative;
  margin: 0 auto $cdr-space-two-x;
  @include cdr-text-heading-serif-600;
  em {
    font-style: italic;
  }
  @include cdr-md-mq-up {
    margin-bottom: $cdr-space-one-x;
    @include cdr-text-heading-serif-800;
  }
}
.member-benefits__subheading {
  @include cdr-text-body-300;
  text-align: center;
  max-width: 660px; // 628px + 32px;
  padding: 0 $cdr-space-one-x;
  margin: 0 auto $cdr-space-one-and-a-half-x;
}
.member-benefits__resource-item li {
  list-style-type: disc;
}
.member-benefits__resource-content {
  margin-bottom: $cdr-space-one-and-a-half-x;

  &:last-child {
    margin-bottom: 0;
  }
}
.member-benefits .member-benefits__resource-link {
  display: block;
}
.member-benefits__new-benefit {
  @include cdr-text-eyebrow-100;
  font-weight: 600;
  display: inline-flex;
  padding: 0 1rem;
  margin-left: 10px;
  background-color: $cdr-color-background-brand-spruce;
  border-radius: 16px;
  height: 20px;
  color: $cdr-color-text-button-primary;
  position: relative;
  top: -1px;

  span {
    margin: 0 auto;
    position: relative;
    top: 1px;
  }
}
</style>
